import { collection, query, where, getDoc, getDocs, addDoc, doc, updateDoc, setDoc, onSnapshot, serverTimestamp } from "firebase/firestore";
import { firestore } from "$lib/firebase/client";
import { User } from "$lib/types/User";
import { omitUndefinedDeep } from "$lib/utils/omitUndefined";
import {
    mapRevisionDocs,
    prepareRevisionWrite,
    restoreUserFromRevision,
    sortRevisionsNewestFirst,
    type UserRevision,
} from "./userRevisions";

export class UserService {
    static instance: UserService | null = null;
    private currentUserUid: string | null = null;

    constructor() {}

    static async queryByEmail(email: string) {
        if (!firestore) throw new Error('Firestore not initialised');
        const q = query(collection(firestore, "users"), where("email", "==", email));

        const querySnapshot = await getDocs(q);
        let users = querySnapshot.docs.map((doc) => ({ ...doc.data(), uid: doc.id }));
        if (users.length > 1) {
            throw Error("More than one user found")
        } else if (users.length === 0) {
            throw Error("User not found")
        } else if (!users[0]) {
            throw Error("User not found")
        }
        return users[0];
    }

    static async getById(id: string): Promise<User> {
        if (!firestore) throw new Error('Firestore not initialised');
        const docRef = doc(firestore, "users", id);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
            throw Error("User does not exist")
        }

        return User.fromJson(docSnap.id, docSnap.data());
    }

    static async update(uid: string, updatedData: any) {
        if (!firestore) throw new Error('Firestore not initialised');
        const userRef = doc(firestore, "users", uid);
        const currentDoc = await getDoc(userRef);

        // store the current data in the history
        const currentData = currentDoc.data();
        const historyCollectionRef = collection(firestore, `users/${uid}/revisions`);
        await addDoc(historyCollectionRef, prepareRevisionWrite(currentData, serverTimestamp()));

        // Firestore rejects undefined anywhere in the payload (including nested fields)
        updatedData = omitUndefinedDeep(updatedData);
        await updateDoc(userRef, updatedData);
    }

    static async listRevisions(uid: string): Promise<UserRevision[]> {
        if (!firestore) throw new Error('Firestore not initialised');
        const snapshot = await getDocs(collection(firestore, `users/${uid}/revisions`));
        return sortRevisionsNewestFirst(mapRevisionDocs(snapshot.docs));
    }

    static async restoreFromRevision(uid: string, revisionData: Record<string, any>): Promise<void> {
        const db = firestore;
        if (!db) throw new Error('Firestore not initialised');
        await restoreUserFromRevision(
            uid,
            revisionData,
            {
                async getUser(id) {
                    const snap = await getDoc(doc(db, 'users', id));
                    return snap.exists() ? snap.data() : undefined;
                },
                async addRevision(id, data) {
                    await addDoc(collection(db, `users/${id}/revisions`), data);
                },
                async replaceUser(id, data) {
                    await setDoc(doc(db, 'users', id), data);
                },
            },
            serverTimestamp(),
        );
    }

    static getBaseUser(email: string) {
        return {
            roles: [],
            mollieSubscriptionId: null,
            expirationDate: null,
            subscriptions: [],
            migrated: true, //all new users are migrated
            email: email,
        };
    }

    static async create(data: any) {
        if (!firestore) throw new Error('Firestore not initialised');
        const userRef = await addDoc(collection(firestore, "users"), data);
        return userRef.id;
    }

    static async subscribeToUserChanges(uid: string, callback: (user: User, unsubscribe: () => void) => void) {
        if (!firestore) throw new Error('Firestore not initialised');
        const userRef = doc(firestore, "users", uid);
        let unsubscribe = onSnapshot(userRef, (docSnap) => {
            let user = User.fromJson(uid, docSnap.data());
            callback(user, unsubscribe);
        });
        return unsubscribe;
    }

    static getInstance() {
        if (UserService.instance == null) {
            UserService.instance = new UserService();
        }
        return UserService.instance;
    }
}
